<template>
  <div class="row">
    <div class="col-md-12">
      <div class="row row-spacing">
        <div
          class="col-md-4"
          v-for="widget in summaryWidgets"
          :key="widget.title"
        >
          <div class="card border-0">
            <div class="row row-flush">
              <div
                :class="getWidgetColour(widget)"
                class="
                  col-4
                  text-center
                  d-flex
                  align-items-center
                  justify-content-center
                  rounded-left
                "
              >
                <em><i :class="getWidgetIcon(widget)"></i></em>
              </div>
              <div class="col-8">
                <div class="card-body text-center">
                  <h4 class="mt-0">{{ widget.value }}</h4>
                  <p class="mb-0 text-muted">{{ widget.title }}</p>
                  <small>{{ widget.subtitle }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row row-spacing">
        <div class="col-md-6">
          <WidgetLayout
            :heading="'Contacts'"
            :icon="'fa fa-users'"
            :showFooterButtons="false"
          >
            <div v-if="prioritizedContacts.length" class="dashboard-list">
              <div
                v-for="contact in prioritizedContacts"
                :key="contact.supplierContactNumber"
                class="dashboard-list-item"
              >
                <div class="font-weight-bold">
                  {{ getContactDisplayName(contact) }}
                </div>
                <small class="text-muted">
                  {{ getAssignedRoleTitles(contact.supplierContactNumber) }}
                </small>
              </div>
              <small
                v-if="contacts.length > contactListLimit"
                class="text-muted d-block mt-2"
              >
                +{{ contacts.length - contactListLimit }} more contact{{
                  contacts.length - contactListLimit === 1 ? "" : "s"
                }}
              </small>
            </div>
            <div v-else class="text-muted">No contacts added yet</div>
          </WidgetLayout>
        </div>

        <div class="col-md-6">
          <WidgetLayout
            :heading="'Domains'"
            :icon="'fa fa-globe'"
            :showFooterButtons="false"
          >
            <div v-if="shortDomains.length" class="dashboard-list">
              <div
                v-for="domain in shortDomains"
                :key="domain.supplierDomainNumber"
                class="dashboard-list-item"
              >
                <div class="font-weight-bold">{{ domain.name }}</div>
              </div>
              <small
                v-if="domains.length > domainListLimit"
                class="text-muted d-block mt-2"
              >
                +{{ domains.length - domainListLimit }} more domain{{
                  domains.length - domainListLimit === 1 ? "" : "s"
                }}
              </small>
            </div>
            <div v-else class="text-muted">No domains added yet</div>
          </WidgetLayout>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WidgetLayout from "@/components/Dashboard/WidgetLayout";

export default {
  components: {
    WidgetLayout,
  },
  data() {
    return {
      supplier: {},
      contacts: [],
      domains: [],
      certificate: null,
      contactListLimit: 5,
      domainListLimit: 5,
      roleConfigs: [
        {
          title: "Accounts",
          supplierField: "accountsNumber",
        },
        {
          title: "Account Manager",
          supplierField: "accountsManagerNumber",
        },
        {
          title: "Service Delivery Manager",
          supplierField: "serviceDeliveryManagerNumber",
        },
        {
          title: "B-BBEE Admin",
          supplierField: "bbbeeAdminNumber",
        },
      ],
    };
  },
  computed: {
    summaryWidgets() {
      return [
        {
          title: "B-BBEE Certificate",
          value: this.certificateStatusLabel,
          subtitle: this.certificateStatusSubtitle,
          icon: this.certificateStatusIcon,
          colour: this.certificateStatusColour,
        },
        {
          title: "Contacts",
          value: this.contacts.length,
          subtitle:
            this.contacts.length === 1
              ? "1 supplier contact"
              : `${this.contacts.length} supplier contacts`,
          icon: "users",
          colour: "blue",
        },
        {
          title: "Domains",
          value: this.domains.length,
          subtitle:
            this.domains.length === 1
              ? "1 supplier domain"
              : `${this.domains.length} supplier domains`,
          icon: "globe",
          colour: "dark-blue",
        },
      ];
    },
    shortDomains() {
      return [...this.domains]
        .sort((left, right) => {
          return (left.name || "").localeCompare(right.name || "", undefined, {
            sensitivity: "base",
          });
        })
        .slice(0, this.domainListLimit);
    },
    prioritizedContacts() {
      return [...this.contacts]
        .sort((left, right) => {
          const leftHasRole = this.contactHasAssignedRole(left) ? 0 : 1;
          const rightHasRole = this.contactHasAssignedRole(right) ? 0 : 1;

          if (leftHasRole !== rightHasRole) {
            return leftHasRole - rightHasRole;
          }

          return this.getContactDisplayName(left).localeCompare(
            this.getContactDisplayName(right),
            undefined,
            { sensitivity: "base" }
          );
        })
        .slice(0, this.contactListLimit);
    },
    certificateStatusLabel() {
      if (!this.hasCertificate) {
        return "Missing";
      }

      if (this.isCertificateExpiringSoon) {
        return "Expiring Soon";
      }

      return this.isCertificateValid ? "Valid" : "Expired";
    },
    certificateStatusSubtitle() {
      if (!this.hasCertificate) {
        return "No certificate uploaded";
      }

      if (!this.certificate.expirationDate || this.isDefaultDate(this.certificate.expirationDate)) {
        return "Expiry date not captured";
      }

      const expiryDate = this.formatDate(this.certificate.expirationDate);
      return this.isCertificateValid
        ? `Valid until ${expiryDate}`
        : `Expired on ${expiryDate}`;
    },
    certificateStatusIcon() {
      if (!this.hasCertificate) {
        return "times-circle";
      }

      if (this.isCertificateExpiringSoon) {
        return "warning";
      }

      return this.isCertificateValid ? "check-circle" : "warning";
    },
    certificateStatusColour() {
      if (!this.hasCertificate) {
        return "orange";
      }

      if (this.isCertificateExpiringSoon) {
        return "orange";
      }

      return this.isCertificateValid ? "green" : "orange";
    },
    hasCertificate() {
      return !!(
        this.certificate &&
        (this.certificate.contentNumber > 0 ||
          this.certificate.supplierDocumentNumber > 0)
      );
    },
    isCertificateValid() {
      if (!this.hasCertificate || !this.certificate.expirationDate) {
        return false;
      }

      if (this.isDefaultDate(this.certificate.expirationDate)) {
        return false;
      }

      const expiryDate = new Date(this.certificate.expirationDate);
      if (Number.isNaN(expiryDate.getTime())) {
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return expiryDate >= today;
    },
    isCertificateExpiringSoon() {
      if (!this.isCertificateValid) {
        return false;
      }

      const expiryDate = new Date(this.certificate.expirationDate);
      const oneMonthFromToday = new Date();
      oneMonthFromToday.setHours(0, 0, 0, 0);
      oneMonthFromToday.setMonth(oneMonthFromToday.getMonth() + 1);

      return expiryDate <= oneMonthFromToday;
    },
  },
  mounted() {
    this.loadPage();
  },
  methods: {
    loadPage() {
      const supplierNumber = this.$route.params.supplierNumber;

      Promise.all([
        this.getSupplier(supplierNumber),
        this.getContacts(supplierNumber),
        this.getDomains(supplierNumber),
        this.getCertificate(supplierNumber),
      ]).catch(() => {
        // Individual requests already surface their own errors.
      });
    },
    getSupplier(supplierNumber) {
      return this.$http.get(this.$config.aimsAPIv3 + "suppliers/" + supplierNumber)
        .then(
          (response) => {
            this.supplier = response.data || {};

            const pageTitle = this.supplier.name
              ? "Supplier Dashboard - " + this.supplier.name
              : "Supplier Dashboard";

            this.$root.$emit("SET_PAGE_TITLE", pageTitle);
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Supplier for Dashboard", error);
          }
        );
    },
    getContacts(supplierNumber) {
      return this.$http.get(
        this.$config.aimsAPIv3 + "suppliers/" + supplierNumber + "/contacts"
      ).then(
        (response) => {
          this.contacts = response.data || [];
        },
        (error) => {
          console.error(error);
          this.showError("Error Fetching Supplier Contacts", error);
        }
      );
    },
    getDomains(supplierNumber) {
      return this.$http.get(
        this.$config.aimsAPIv3 + "suppliers/" + supplierNumber + "/domains"
      ).then(
        (response) => {
          this.domains = response.data || [];
        },
        (error) => {
          console.error(error);
          this.showError("Error Fetching Supplier Domains", error);
        }
      );
    },
    getCertificate(supplierNumber) {
      return this.$http.get(
        this.$config.aimsAPIv3 + "suppliers/" + supplierNumber + "/certificate"
      ).then(
        (response) => {
          this.certificate = response.data;
        },
        (error) => {
          console.error(error);
          this.showError("Error Fetching Supplier B-BBEE Certificate", error);
        }
      );
    },
    getContactDisplayName(contact) {
      if (!contact) {
        return "";
      }

      const name = [contact.firstNames, contact.lastName]
        .filter((part) => !!part && part.trim() !== "")
        .join(" ")
        .trim();

      if (name) {
        return name;
      }

      return contact.emailAddress || `Contact #${contact.supplierContactNumber}`;
    },
    contactHasAssignedRole(contact) {
      return this.roleConfigs.some((role) => {
        return this.supplier[role.supplierField] === contact.supplierContactNumber;
      });
    },
    getAssignedRoleTitles(supplierContactNumber) {
      const titles = [];

      for (let i = 0; i < this.roleConfigs.length; i++) {
        const role = this.roleConfigs[i];
        if (this.supplier[role.supplierField] === supplierContactNumber) {
          titles.push(role.title);
        }
      }

      return titles.length ? titles.join(", ") : "General Contact";
    },
    formatDate(value) {
      if (!value) {
        return "";
      }

      return value.toString().split(" ")[0];
    },
    isDefaultDate(value) {
      return this.formatDate(value) === "1970-01-01";
    },
    getWidgetIcon(widget) {
      return `fa fa-${widget.icon} fa-2x`;
    },
    getWidgetColour(widget) {
      switch (widget.colour) {
        case "green":
          return "bg-green";
        case "blue":
          return "bg-info";
        case "orange":
          return "bg-warning";
        case "gold":
          return "bg-gold";
        case "grey":
          return "bg-primary";
        case "dark-blue":
          return "bg-primary-dark";
        default:
          break;
      }
    },
  },
};
</script>

<style scoped>
.dashboard-list-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid #edf1f5;
}

.dashboard-list-item:first-child {
  padding-top: 0;
}

.dashboard-list-item:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}
</style>
